import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PaymentPage.css';

function PaymentPage() {
  const { qrcode } = useParams();
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestMadeRef = useRef(false);
  const loadingStartTimeRef = useRef(Date.now());

  // Set API URL - will use proxy in development
  const apiUrl = process.env.REACT_APP_API_URL || '';

  // Clean up any leftover payment forms and check for stuck state on mount
  useEffect(() => {
    // Remove any payment forms that might be stuck in the DOM
    const existingForms = document.querySelectorAll('form[action*="authorize.net"]');
    existingForms.forEach(form => {
      const parentDiv = form.parentElement;
      if (parentDiv && parentDiv !== document.body) {
        parentDiv.remove();
      } else {
        form.remove();
      }
    });

    // If there were stuck forms, this means we're returning from payment
    // Reset the requestMadeRef so the fetch can happen
    if (existingForms.length > 0) {
      console.log('Found stuck payment forms, resetting state...');
      requestMadeRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!requestMadeRef.current) {
      // Fetch invoice details using POST request with qrcode in body
      const fetchInvoiceInfo = async () => {
        try {
          setLoading(true);
          const response = await fetch(`${apiUrl}/api/invoice`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              qrcode: qrcode
            })
          });

          if (!response.ok) {
            throw new Error('Invalid invoice link or expired session');
          }

          const data = await response.json();
          console.log('Invoice data received:', data);

          setInvoiceData(data);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching invoice:', err);
          setError(err.message);
          setLoading(false);
        }
      };

      if (qrcode) {
        console.log('Fetching invoice info for QR code:', qrcode);
        requestMadeRef.current = true; // Mark that the request has been made
        fetchInvoiceInfo();
      } else {
        console.log('No QR code provided');
        setLoading(false);
      }
    }
  }, [qrcode, apiUrl]);

  // Auto-reload if stuck in loading state - Multiple strategies
  useEffect(() => {
    let timeoutId;
    let intervalId;

    // Strategy 1: Simple timeout after 2 seconds
    if (loading && !invoiceData && !error) {
      console.log('Setting up auto-reload timer...');
      timeoutId = setTimeout(() => {
        console.log('Page stuck loading for 2 seconds, forcing reload...');
        window.location.reload();
      }, 2000);

      // Strategy 2: Check every 500ms if we're stuck
      intervalId = setInterval(() => {
        const timeElapsed = Date.now() - loadingStartTimeRef.current;
        if (timeElapsed > 2000 && loading && !invoiceData && !error) {
          console.log('Detected stuck state via interval check, forcing reload...');
          window.location.reload();
        }
      }, 500);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [loading, invoiceData, error]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('/');
    if (parts.length !== 3 && parts.length !== 2) return dateString;

    if (parts.length === 3) {
      return `${parts[0]}/${parts[1]}/${parts[2]}`;
    } else {
      return dateString; // Return as is if it's MM/YYYY format
    }
  };

  const calculateTotal = () => {
    if (!invoiceData) return 0;
    const total = parseFloat(invoiceData.clbld) || 0;
    return total;
  };

  // Check if payment should be disabled
  const isPaymentDisabled = () => {
    if (!invoiceData) return true;

    // Check if status is "Paid" (case-insensitive)
    if (invoiceData.status && invoiceData.status.toLowerCase() === "paid") {
      return true;
    }

    // Check if amount is less than or equal to 0
    const total = calculateTotal();
    if (total <= 0) {
      return true;
    }

    return false;
  };

  // Get reason why payment is disabled
  const getPaymentDisabledReason = () => {
    if (!invoiceData) return "";

    if (invoiceData.status && invoiceData.status.toLowerCase() === "paid") {
      return "This invoice has already been paid.";
    }

    const total = calculateTotal();
    if (total <= 0) {
      return "No payment is required for this invoice.";
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Double-check if payment should be disabled
    if (isPaymentDisabled()) {
      return;
    }

    // Validate invoice ID
    if (!invoiceData._id) {
      setError('Invalid invoice ID');
      return;
    }

    // Validate amount
    const total = calculateTotal();
    if (isNaN(total) || total <= 0) {
      setError('Invalid payment amount');
      return;
    }

    // Create and log payload for debugging
    const payload = {
      qrcode,
      invoiceId: invoiceData._id,
      amount: total
    };
    console.log('Payment payload:', payload);

    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/api/process-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Payment processing failed with status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Create a temporary div to hold the form
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = result.paymentform;
        document.body.appendChild(tempDiv);

        // Set a timeout to allow the DOM to update
        setTimeout(() => {
          // Find and click the submit button
          const submitButton = document.getElementById('submitpaymentclick');
          if (submitButton) {
            submitButton.click();
          } else {
            setError('Payment form could not be processed');
            setLoading(false);
          }
        }, 100);
      } else {
        throw new Error(result.detail || 'Payment processing failed');
      }

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-container loading">
        <div style={{ textAlign: 'center' }}>
          <p>Loading invoice information...</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#4F46E5',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4338CA'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#4F46E5'}
          >
            🔄 Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="payment-container error">Error: {error}</div>;
  }

  if (!invoiceData) {
    return <div className="payment-container error">No invoice data available</div>;
  }

  return (
    <div className="payment-container">
      <div className="payment-header">
        <h1>Invoice Payment Portal</h1>
        <div className="company-details">
          <h2>Emerson H Lee</h2>
          <p>Professional Accounting Services</p>
          <p>703-237-5404</p>
        </div>
      </div>

      <div className="invoice-details">
        <div className="client-info">
          <h3>Bill To:</h3>
          <p className="client-name">{invoiceData.name}</p>
          <p>{invoiceData.address}</p>
          <p>{invoiceData.city}, {invoiceData.state} {invoiceData.zipcode}</p>
          <p>Account #: {invoiceData.accountnum}</p>
        </div>

        <div className="invoice-summary">
          <div className="summary-item">
            <span>Invoice #:</span>
            <span>{invoiceData.invoice}</span>
          </div>
          <div className="summary-item">
            <span>Statement Date:</span>
            <span>{formatDate(invoiceData.statementdate)}</span>
          </div>
          <div className="summary-item">
            <span>Period:</span>
            <span>{invoiceData.month}/{invoiceData.year}</span>
          </div>
          <div className="summary-item">
            <span>Status:</span>
            <span className={invoiceData.status && invoiceData.status.toLowerCase() === "paid" ? "status-paid" : "status-unpaid"}>
              {invoiceData.status}
            </span>
          </div>
        </div>
      </div>

      <div className="invoice-items">
        <h3>Invoice Details</h3>
        <table className="items-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Check #</th>
              <th>Type</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoiceData.items && invoiceData.items.map((item, index) => (
              <tr key={item.itemid || index} className={item.type === "receive" ? "payment-row" : "charge-row"}>
                <td>{formatDate(item.date)}</td>
                <td>{item.description}</td>
                <td>{item.checknum || "-"}</td>
                <td>{item.type === "receive" ? "Payment" : "Charge"}</td>
                <td className={item.type === "receive" ? "payment-amount" : "charge-amount"}>
                  ${parseFloat(item.amount).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="invoice-summary-totals">
        <div className="summary-line">
          <span>Subtotal:</span>
          <span>${parseFloat(invoiceData.subtotal || 0).toFixed(2)}</span>
        </div>
        <div className="summary-line">
          <span>Previous Balance:</span>
          <span>${parseFloat(invoiceData.blfw || 0).toFixed(2)}</span>
        </div>
        <div className="summary-line total">
          <span>Current Balance Due:</span>
          <span>${parseFloat(invoiceData.clbld || 0).toFixed(2)}</span>
        </div>

        <div style={{ textAlign: 'right', marginTop: '15px' }}>
          {isPaymentDisabled() ? (
            <div className="payment-disabled-message">{getPaymentDisabledReason()}</div>
          ) : (
            <button
              type="button"
              className="pay-button"
              onClick={handleSubmit}
              disabled={isPaymentDisabled() || loading}
            >
              {loading ? 'Processing payment...' : `Pay $${calculateTotal().toFixed(2)}`}
            </button>
          )}
        </div>
      </div>

      <div className="payment-footer">
        <p>For questions about this invoice, please contact our office at (703) 237-5404.</p>
        <p>Thank you for your business!</p>
        <button
          onClick={() => navigate('/')}
          className="home-button"
          style={{
            marginTop: '20px',
            padding: '12px 24px',
            backgroundColor: '#4F46E5',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#4338CA'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#4F46E5'}
        >
          ← Back to Home
        </button>
      </div>
    </div>
  );
}

export default PaymentPage;